import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Save, Upload, Building2, Image as ImageIcon, Sliders } from "lucide-react";
import { toast } from "sonner";

export default function InvoiceSettings() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company_name: "",
    business_id: "",
    address: "",
    postal_code: "",
    city: "",
    email: "",
    phone: "",
    bank_account: "",
    bic: "",
    logo_url: "",
    default_payment_terms: 14,
    default_penalty_interest: 7,
    default_vat_percent: 25.5,
    next_invoice_number: 1,
    next_reference_number: 100000001,
    invoice_prefix: "",
    logo_position: "corner",
    logo_opacity: 15,
    logo_width: 200,
    logo_height: 80,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/settings/invoice");
        setForm((p) => ({ ...p, ...(r.data || {}) }));
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const HARD_MAX = 10 * 1024 * 1024; // 10 MB
    if (file.size > HARD_MAX) {
      toast.error("Logo on liian iso (max 10 MB)");
      return;
    }
    try {
      const dataUrl = await readAndResizeImage(file, 800);
      setForm((p) => ({ ...p, logo_url: dataUrl }));
      toast.success("Logo ladattu (automaattisesti optimoitu)");
    } catch (err) {
      toast.error("Logon käsittely epäonnistui");
    }
  };

  // Pakkaa & pienennä iso kuva selaimessa ennen tallennusta.
  // Skaalaa max-leveyteen, säilyttää suhteen, tallentaa JPEG laadulla 0.85
  // (tai PNG jos läpinäkyvyyttä epäillään).
  const readAndResizeImage = (file, maxWidth = 800) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = (ev) => {
        const img = new Image();
        img.onerror = reject;
        img.onload = () => {
          // Pienet kuvat jätetään ennalleen (säilytetään läpinäkyvyys & laatu)
          if (img.width <= maxWidth && file.size <= 1024 * 1024) {
            return resolve(ev.target.result);
          }
          const scale = Math.min(1, maxWidth / img.width);
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          ctx.drawImage(img, 0, 0, w, h);
          const isPng = file.type === "image/png";
          const out = isPng
            ? canvas.toDataURL("image/png")
            : canvas.toDataURL("image/jpeg", 0.85);
          resolve(out);
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/invoice", form);
      toast.success("Asetukset tallennettu");
      navigate("/invoicing");
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto space-y-6" data-testid="invoice-settings">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/invoicing")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-2xl md:text-3xl font-display font-bold text-amber-100">Laskutusasetukset</h1>
      </div>

      <Tabs defaultValue="company">
        <TabsList className="bg-zinc-900/60 border border-amber-500/20 rounded-xl p-1">
          <TabsTrigger value="company" className="gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-900">
            <Building2 className="w-4 h-4" /> Yritystiedot
          </TabsTrigger>
          <TabsTrigger value="logo" className="gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-900">
            <ImageIcon className="w-4 h-4" /> Logo & Ulkoasu
          </TabsTrigger>
          <TabsTrigger value="defaults" className="gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-900">
            <Sliders className="w-4 h-4" /> Oletusasetukset
          </TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Yrityksen tiedot</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Yrityksen nimi</Label>
                  <Input
                    value={form.company_name || ""}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    data-testid="settings-company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Y-tunnus</Label>
                  <Input value={form.business_id || ""} onChange={(e) => setForm({ ...form, business_id: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Osoite</Label>
                  <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label>Postinumero</Label>
                    <Input value={form.postal_code || ""} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Kaupunki</Label>
                    <Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Sähköposti</Label>
                  <Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Puhelin</Label>
                  <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>IBAN-tilinumero</Label>
                  <Input
                    value={form.bank_account || ""}
                    onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
                    placeholder="FI21 1234 5600 0007 85"
                    data-testid="settings-iban"
                  />
                </div>
                <div className="space-y-2">
                  <Label>BIC</Label>
                  <Input value={form.bic || ""} onChange={(e) => setForm({ ...form, bic: e.target.value })} placeholder="NDEAFIHH" />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logo" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Logo & Ulkoasuasetukset</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label>Yrityksen logo</Label>
                <div className="flex items-center gap-4 flex-wrap">
                  {form.logo_url && (
                    <img src={form.logo_url} alt="Logo" className="h-16 object-contain rounded border border-amber-500/20 bg-white p-1" />
                  )}
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="settings-logo-upload" />
                    <Button variant="outline" size="sm" className="gap-1" asChild>
                      <span>
                        <Upload className="w-3 h-3" /> Lataa logo
                      </span>
                    </Button>
                  </label>
                  {form.logo_url && (
                    <Button variant="ghost" size="sm" className="text-destructive text-xs" onClick={() => setForm({ ...form, logo_url: "" })}>
                      Poista
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Max 10 MB. PNG / JPG / SVG. Suuret kuvat pakataan automaattisesti.</p>
              </div>

              {form.logo_url && (
                <>
                  <div className="space-y-3">
                    <Label>Logon sijainti laskulla</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { value: "corner", label: "Kulmassa", desc: "Logo näkyy laskun yläkulmassa" },
                        { value: "background", label: "Taustakuvana", desc: "Logo näkyy vaaleana taustana" },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setForm({ ...form, logo_position: opt.value })}
                          className={`text-left p-3 rounded-xl border transition-all ${
                            form.logo_position === opt.value ? "border-amber-500 bg-amber-500/10" : "border-zinc-700 hover:border-amber-500/40"
                          }`}
                        >
                          <p className="font-semibold text-sm">{opt.label}</p>
                          <p className="text-xs text-muted-foreground mt-1">{opt.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label>Leveys: {form.logo_width}px</Label>
                      <Slider
                        min={50}
                        max={500}
                        step={10}
                        value={[form.logo_width]}
                        onValueChange={([v]) => setForm({ ...form, logo_width: v })}
                      />
                    </div>
                    <div className="space-y-3">
                      <Label>Korkeus: {form.logo_height}px</Label>
                      <Slider
                        min={30}
                        max={300}
                        step={10}
                        value={[form.logo_height]}
                        onValueChange={([v]) => setForm({ ...form, logo_height: v })}
                      />
                    </div>
                  </div>

                  {form.logo_position === "background" && (
                    <div className="space-y-3">
                      <Label>Taustakuvan läpinäkyvyys: {form.logo_opacity}%</Label>
                      <Slider
                        min={3}
                        max={40}
                        step={1}
                        value={[form.logo_opacity]}
                        onValueChange={([v]) => setForm({ ...form, logo_opacity: v })}
                      />
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="defaults" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Oletusasetukset</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Maksuehto (pv)</Label>
                  <Input
                    type="number"
                    value={form.default_payment_terms}
                    onChange={(e) => setForm({ ...form, default_payment_terms: parseInt(e.target.value) || 14 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Viivästyskorko %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={form.default_penalty_interest}
                    onChange={(e) => setForm({ ...form, default_penalty_interest: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>ALV %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={form.default_vat_percent}
                    onChange={(e) => setForm({ ...form, default_vat_percent: parseFloat(e.target.value) || 25.5 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Laskun etuliite</Label>
                  <Input
                    value={form.invoice_prefix || ""}
                    onChange={(e) => setForm({ ...form, invoice_prefix: e.target.value })}
                    placeholder="esim. INV-"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Seuraava laskunumero</Label>
                  <Input
                    type="number"
                    value={form.next_invoice_number}
                    onChange={(e) => setForm({ ...form, next_invoice_number: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Seuraava viitenumero</Label>
                  <Input
                    type="number"
                    value={form.next_reference_number}
                    onChange={(e) => setForm({ ...form, next_reference_number: parseInt(e.target.value) || 100000001 })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="gap-2 bg-amber-500 hover:bg-amber-400 text-zinc-900 font-medium" data-testid="settings-save">
          <Save className="w-4 h-4" /> Tallenna asetukset
        </Button>
      </div>
    </div>
  );
}
